/**
 * V2 Medal table — /medals (2026-08-07, the "who has the most gold,
 * silver and bronze over all" ask).
 *
 * The site-wide standings: every explorer's medal tally across all
 * categories, Olympic ordering (golds, then silvers, then bronzes),
 * served ranked by the get_medal_table RPC
 * (supabase/migrations/20260807a_medal_table.sql). Until that SQL runs
 * the RPC 404s and the page shows its quiet empty state — the
 * lists/trophies degradation pattern. Public, viewable logged out;
 * reached from every trophy case's "Overall medal table" link. Rows
 * open that explorer's trophy case; your own row is highlighted.
 */
import { escapeHtml } from '../../../assets/js/utils.js';
import { shareButton, handleShareClick } from '../../ui/share.js';
import { trophyMedal, TROPHY_MEDALS, deriveMedalTable } from './profile-derive.js';
import { fetchMedalTable } from './profile-data.js';
import { setContext as setRibbonContext } from '../../ui/visitor-ribbon.js';

export function mount(el, ctx) {
  const { store, router } = ctx;
  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';
  let destroyed = false;

  function row(r, meId) {
    const me = meId && r.userId === meId;
    return `
      <button type="button" class="medal-row${me ? ' medal-row-me' : ''}"
        data-trophy-user="${escapeHtml(r.username)}"
        aria-label="#${r.rank} ${escapeHtml(r.name)}${me ? ' (you)' : ''} — ${r.gold} gold, ${r.silver} silver, ${r.bronze} bronze. Open their trophy case.">
        <span class="medal-pos">#${r.rank}</span>
        <span class="medal-who">${escapeHtml(r.name)}${me ? ' <span class="medal-you">you</span>' : ''}</span>
        <span class="medal-count">${r.gold}</span>
        <span class="medal-count">${r.silver}</span>
        <span class="medal-count">${r.bronze}</span>
      </button>`;
  }

  function render(standings) {
    document.title = 'Medal table | Yoink Adventures v2';
    setRibbonContext('The top collectors across every category. Start your own tally.');
    const meId = store.get().user?.id || null;
    // Column meanings live in the header TEXT; the medals only decorate
    // (art direction: stamps and icons decorate, text carries meaning)
    const table = standings.length ? `
      <div class="medal-table">
        <div class="medal-row medal-head" aria-hidden="true">
          <span class="medal-pos"></span>
          <span class="medal-who"></span>
          ${[1, 2, 3].map((rank) => `<span class="medal-count">${trophyMedal(rank, 18)}<br>${TROPHY_MEDALS[rank]}</span>`).join('')}
        </div>
        ${standings.map((r) => row(r, meId)).join('')}
      </div>
      <p class="log-hint">Top 50. A medal is a top-3 finish in a category — most golds first, silvers break ties.</p>`
      : '<p class="log-hint">The medal table hasn’t been engraved yet — check back soon.</p>';
    el.innerHTML = `
      <nav class="v2-back" aria-label="Back">
        <a href="${basePath}/" data-route="discover">← Discover</a>
        ${shareButton('The Yoink Adventures medal table')}
      </nav>
      <header class="v2-category-head">
        <div class="cat-head-names">
          <h2 class="cat-head-name">Medal table</h2>
          <p class="cat-head-desc">Every explorer’s podium finishes, side by side. Tap a row to see the trophy case behind it.</p>
        </div>
      </header>
      <section class="showcase-body showcase-medals">${table}</section>`;
  }

  function onClick(event) {
    if (handleShareClick(event)) return;
    const rowEl = event.target.closest('[data-trophy-user]');
    if (rowEl) {
      router.navigate('user-trophies', { username: rowEl.dataset.trophyUser });
      return;
    }
    const backLink = event.target.closest('a[data-route]');
    if (backLink) {
      event.preventDefault();
      router.navigate(backLink.dataset.route);
    }
  }

  el.addEventListener('click', onClick);
  el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
  fetchMedalTable()
    .then((rows) => {
      if (!destroyed) render(deriveMedalTable(rows));
    })
    .catch(() => {
      // RPC not applied yet → the quiet empty state, never an error page
      if (!destroyed) render([]);
    });

  return () => {
    destroyed = true;
    el.removeEventListener('click', onClick);
  };
}
